import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';
import {
  BlogPost,
  BlogCategory,
  BlogTag,
  BlogComment,
  BlogPostCreate,
  BlogCategoryCreate,
  BlogTagCreate
} from '../types';
import { extractApiError } from '../utils/errorUtils';
import ConfirmDialog from './ConfirmDialog';
import { useConfirmDialog } from '../hooks/useConfirmDialog';

/**
 * AdminBlogManager Component
 *
 * Admin interface for managing blog platform
 * Issue #1386 - Blog platform frontend
 *
 * Features:
 * - Manage blog posts (create, edit, delete, publish)
 * - Manage categories and tags
 * - Moderate comments
 * - View analytics
 */

type Tab = 'posts' | 'categories' | 'tags' | 'comments';

const AdminBlogManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('posts');

  // Posts state
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [postsPage, setPostsPage] = useState(1);
  const [postsPageSize] = useState(25);
  const [postsTotal, setPostsTotal] = useState(0);

  // Categories state
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState<BlogCategoryCreate>({
    name: '',
    slug: '',
    description: ''
  });

  // Tags state
  const [tags, setTags] = useState<BlogTag[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [showTagForm, setShowTagForm] = useState(false);
  const [tagFormData, setTagFormData] = useState<BlogTagCreate>({
    name: '',
    slug: ''
  });

  // Comments state
  const [pendingComments, setPendingComments] = useState<BlogComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);

  // Confirm dialog hook
  const { confirmDialog, showConfirm, closeDialog } = useConfirmDialog();

  // Post editor state
  const [showPostEditor, setShowPostEditor] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [postFormData, setPostFormData] = useState<BlogPostCreate>({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    published: false,
    meta_title: '',
    meta_description: '',
    featured_image_url: '',
    category_ids: [],
    tag_ids: []
  });

  // Fetch functions
  const fetchPosts = useCallback(async () => {
    setLoadingPosts(true);
    try {
      const response = await api.getBlogPosts({
        published_only: false,
        page: postsPage,
        page_size: postsPageSize
      });
      setPosts(response.posts);
      setPostsTotal(response.total);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      toast.error('Failed to load posts');
      console.error(apiError);
    } finally {
      setLoadingPosts(false);
    }
  }, [postsPage, postsPageSize]);

  const fetchCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const data = await api.getBlogCategories();
      setCategories(data);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      toast.error('Failed to load categories');
      console.error(apiError);
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  const fetchTags = useCallback(async () => {
    setLoadingTags(true);
    try {
      const data = await api.getBlogTags();
      setTags(data);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      toast.error('Failed to load tags');
      console.error(apiError);
    } finally {
      setLoadingTags(false);
    }
  }, []);

  const fetchPendingComments = useCallback(async () => {
    setLoadingComments(true);
    try {
      const data = await api.getPendingBlogComments();
      setPendingComments(data);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      toast.error('Failed to load pending comments');
      console.error(apiError);
    } finally {
      setLoadingComments(false);
    }
  }, []);

  // Load data based on active tab
  useEffect(() => {
    switch (activeTab) {
      case 'posts':
        fetchPosts();
        break;
      case 'categories':
        fetchCategories();
        break;
      case 'tags':
        fetchTags();
        break;
      case 'comments':
        fetchPendingComments();
        break;
    }
  }, [activeTab, postsPage, fetchPosts, fetchCategories, fetchTags, fetchPendingComments]);

  // Category handlers
  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.createBlogCategory(categoryFormData);
      toast.success('Category created');
      setCategoryFormData({ name: '', slug: '', description: '' });
      setShowCategoryForm(false);
      fetchCategories();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      toast.error('Failed to create category');
      console.error(apiError);
    }
  };

  // Tag handlers
  const handleCreateTag = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.createBlogTag(tagFormData);
      toast.success('Tag created');
      setTagFormData({ name: '', slug: '' });
      setShowTagForm(false);
      fetchTags();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      toast.error('Failed to create tag');
      console.error(apiError);
    }
  };

  // Comment handlers
  const handleApproveComment = async (commentId: number) => {
    try {
      await api.approveBlogComment(commentId);
      toast.success('Comment approved');
      fetchPendingComments();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      toast.error('Failed to approve comment');
      console.error(apiError);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    showConfirm({
      title: 'Delete Comment',
      message: 'Are you sure you want to delete this comment? This action cannot be undone.',
      variant: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await api.deleteBlogComment(commentId);
          toast.success('Comment deleted');
          fetchPendingComments();
        } catch (err: unknown) {
          const apiError = extractApiError(err);
          toast.error('Failed to delete comment');
          console.error(apiError);
          throw err; // Re-throw so hook can handle processing state
        }
      }
    });
  };

  // Post handlers
  const handleCreatePost = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.createBlogPost(postFormData);
      toast.success('Post created');
      setShowPostEditor(false);
      setPostFormData({
        title: '',
        slug: '',
        content: '',
        excerpt: '',
        published: false,
        meta_title: '',
        meta_description: '',
        featured_image_url: '',
        category_ids: [],
        tag_ids: []
      });
      setPostsPage(1); // Reset to first page
      fetchPosts();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      toast.error('Failed to create post');
      console.error(apiError);
    }
  };

  const handleUpdatePost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPost) return;

    try {
      await api.updateBlogPost(editingPost.id, postFormData);
      toast.success('Post updated');
      setShowPostEditor(false);
      setEditingPost(null);
      fetchPosts();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      toast.error('Failed to update post');
      console.error(apiError);
    }
  };

  const handleDeletePost = async (postId: number) => {
    showConfirm({
      title: 'Delete Post',
      message: 'Are you sure you want to delete this post? This action cannot be undone.',
      variant: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await api.deleteBlogPost(postId);
          toast.success('Post deleted');
          // If we're on the last page and delete the only item, go back one page
          if (posts.length === 1 && postsPage > 1) {
            setPostsPage(postsPage - 1);
          }
          fetchPosts();
        } catch (err: unknown) {
          const apiError = extractApiError(err);
          toast.error('Failed to delete post');
          console.error(apiError);
          throw err; // Re-throw so hook can handle processing state
        }
      }
    });
  };

  const openPostEditor = (post?: BlogPost) => {
    if (post) {
      setEditingPost(post);
      setPostFormData({
        title: post.title,
        slug: post.slug,
        content: post.content,
        excerpt: post.excerpt || '',
        published: post.published || false,
        meta_title: post.meta_title || '',
        meta_description: post.meta_description || '',
        featured_image_url: post.featured_image_url || '',
        category_ids: post.categories?.map(c => c.id) || [],
        tag_ids: post.tags?.map(t => t.id) || []
      });
    } else {
      setEditingPost(null);
      setPostFormData({
        title: '',
        slug: '',
        content: '',
        excerpt: '',
        published: false,
        meta_title: '',
        meta_description: '',
        featured_image_url: '',
        category_ids: [],
        tag_ids: []
      });
    }
    setShowPostEditor(true);
  };

  const autoGenerateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Blog Management</h1>
        <p className="text-gray-600 mt-2">Manage blog posts, categories, tags, and comments</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {(['posts', 'categories', 'tags', 'comments'] as Tab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`${
                activeTab === tab
                  ? 'border-primary text-secondary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`}
            >
              {tab}
              {tab === 'comments' && pendingComments.length > 0 && (
                <span className="ml-2 bg-red-100 text-red-800 py-0.5 px-2 rounded-full text-xs">
                  {pendingComments.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Posts Tab */}
      {activeTab === 'posts' && (
        <div>
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold">Blog Posts</h2>
            <button
              onClick={() => openPostEditor()}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              Create Post
            </button>
          </div>

          {loadingPosts ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : posts.length > 0 ? (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comments</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {posts.map((post) => (
                    <tr key={post.id}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{post.title}</div>
                        <div className="text-sm text-gray-500">/blog/{post.slug}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          post.published ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {post.published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{post.view_count || 0}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{post.comment_count || 0}</td>
                      <td className="px-6 py-4 text-sm font-medium space-x-2">
                        {post.published && (
                          <Link
                            to={`/blog/${post.slug}`}
                            target="_blank"
                            className="text-secondary hover:text-teal-900"
                          >
                            View
                          </Link>
                        )}
                        <button
                          onClick={() => openPostEditor(post)}
                          className="text-primary hover:text-blue-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeletePost(post.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg">
              <p className="text-gray-500">No posts yet. Create your first post!</p>
            </div>
          )}

          {/* Pagination */}
          {postsTotal > postsPageSize && (
            <div className="mt-6 flex items-center justify-between bg-white px-4 py-3 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <span>
                  Showing {Math.min((postsPage - 1) * postsPageSize + 1, postsTotal)} to{' '}
                  {Math.min(postsPage * postsPageSize, postsTotal)} of {postsTotal} posts
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPostsPage(Math.max(1, postsPage - 1))}
                  disabled={postsPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Previous page"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {postsPage} of {Math.ceil(postsTotal / postsPageSize)}
                </span>
                <button
                  onClick={() => setPostsPage(Math.min(Math.ceil(postsTotal / postsPageSize), postsPage + 1))}
                  disabled={postsPage >= Math.ceil(postsTotal / postsPageSize)}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Next page"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div>
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold">Categories</h2>
            <button
              onClick={() => setShowCategoryForm(!showCategoryForm)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              {showCategoryForm ? 'Cancel' : 'Add Category'}
            </button>
          </div>

          {showCategoryForm && (
            <form onSubmit={handleCreateCategory} className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    value={categoryFormData.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setCategoryFormData({
                        ...categoryFormData,
                        name,
                        slug: autoGenerateSlug(name)
                      });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Slug</label>
                  <input
                    type="text"
                    value={categoryFormData.slug}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, slug: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={categoryFormData.description}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                Create Category
              </button>
            </form>
          )}

          {loadingCategories ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : categories.length > 0 ? (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {categories.map((category) => (
                    <tr key={category.id}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{category.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{category.slug}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{category.description || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg">
              <p className="text-gray-500">No categories yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Tags Tab */}
      {activeTab === 'tags' && (
        <div>
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold">Tags</h2>
            <button
              onClick={() => setShowTagForm(!showTagForm)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              {showTagForm ? 'Cancel' : 'Add Tag'}
            </button>
          </div>

          {showTagForm && (
            <form onSubmit={handleCreateTag} className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    value={tagFormData.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setTagFormData({
                        ...tagFormData,
                        name,
                        slug: autoGenerateSlug(name)
                      });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Slug</label>
                  <input
                    type="text"
                    value={tagFormData.slug}
                    onChange={(e) => setTagFormData({ ...tagFormData, slug: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                Create Tag
              </button>
            </form>
          )}

          {loadingTags ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : tags.length > 0 ? (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tags.map((tag) => (
                    <tr key={tag.id}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{tag.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{tag.slug}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg">
              <p className="text-gray-500">No tags yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Comments Tab */}
      {activeTab === 'comments' && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Pending Comments</h2>

          {loadingComments ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : pendingComments.length > 0 ? (
            <div className="space-y-4">
              {pendingComments.map((comment) => (
                <div key={comment.id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {comment.author?.name || comment.author_name || 'Anonymous'}
                      </p>
                      {comment.author_email && (
                        <p className="text-sm text-gray-600">{comment.author_email}</p>
                      )}
                      <p className="text-sm text-gray-500 mt-1">
                        {new Date(comment.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApproveComment(comment.id)}
                        className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{comment.content}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg">
              <p className="text-gray-500">No pending comments.</p>
            </div>
          )}
        </div>
      )}

      {/* Post Editor Modal */}
      {showPostEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl my-8">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingPost ? 'Edit Post' : 'Create Post'}
              </h2>
            </div>

            <form onSubmit={editingPost ? handleUpdatePost : handleCreatePost} className="p-6">
              {/* Title & Slug */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    value={postFormData.title}
                    onChange={(e) => {
                      const title = e.target.value;
                      setPostFormData({
                        ...postFormData,
                        title,
                        slug: editingPost ? postFormData.slug : autoGenerateSlug(title)
                      });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Slug *</label>
                  <input
                    type="text"
                    value={postFormData.slug}
                    onChange={(e) => setPostFormData({ ...postFormData, slug: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Excerpt */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Excerpt</label>
                <textarea
                  value={postFormData.excerpt}
                  onChange={(e) => setPostFormData({ ...postFormData, excerpt: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Brief summary of the post..."
                />
              </div>

              {/* Content */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Content * (HTML)</label>
                <textarea
                  value={postFormData.content}
                  onChange={(e) => setPostFormData({ ...postFormData, content: e.target.value })}
                  rows={12}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
                  required
                  placeholder="<p>Your blog content here...</p>"
                />
                <p className="text-sm text-gray-500 mt-1">Use HTML tags for formatting. Content will be sanitized on the backend.</p>
              </div>

              {/* Featured Image */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Featured Image URL</label>
                <input
                  type="url"
                  value={postFormData.featured_image_url}
                  onChange={(e) => setPostFormData({ ...postFormData, featured_image_url: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              {/* SEO Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Meta Title</label>
                  <input
                    type="text"
                    value={postFormData.meta_title}
                    onChange={(e) => setPostFormData({ ...postFormData, meta_title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="SEO title (defaults to post title)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Meta Description</label>
                  <input
                    type="text"
                    value={postFormData.meta_description}
                    onChange={(e) => setPostFormData({ ...postFormData, meta_description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="SEO description (defaults to excerpt)"
                  />
                </div>
              </div>

              {/* Categories & Tags */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Categories</label>
                  <select
                    multiple
                    value={postFormData.category_ids?.map(String) || []}
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                      setPostFormData({ ...postFormData, category_ids: selectedOptions });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    size={4}
                  >
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                  <select
                    multiple
                    value={postFormData.tag_ids?.map(String) || []}
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                      setPostFormData({ ...postFormData, tag_ids: selectedOptions });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    size={4}
                  >
                    {tags.map((tag) => (
                      <option key={tag.id} value={tag.id}>
                        {tag.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple</p>
                </div>
              </div>

              {/* Published Checkbox */}
              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={postFormData.published}
                    onChange={(e) => setPostFormData({ ...postFormData, published: e.target.checked })}
                    className="rounded border-gray-300 text-secondary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Publish this post</span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-4">
                <button
                  type="submit"
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                  {editingPost ? 'Update Post' : 'Create Post'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPostEditor(false);
                    setEditingPost(null);
                  }}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeDialog}
        isProcessing={confirmDialog.isProcessing}
      />
    </div>
  );
};

export default AdminBlogManager;
